import classNames from 'classnames';
import isString from 'lodash-es/isString';
import { useCallback, useEffect, useState } from 'react';

import useFormatImageUrl from '../../hooks/useFormatImageUrl';
import getImageSize from '../../lib/getImageSize';
import isInstantAnswerField from '../../lib/isInstantAnswerField';

import FormButton from '../../components/buttons/FormButton';
import FormQuestion from '../../components/forms/FormQuestion';
import FormStatus from '../../components/partials/FormStatus';

import styles from './question.module.css';

interface SurveyQuestionProps {
    field: FormField;
    group?: FormField;
    // image?: Image;
    url?: string;
    answer?: {
        value: unknown;
    } | null;
    errors?: string[] | null;
    className?: string;
    onAnswer?: (newAnswer: unknown) => Promise<FormSubmissionAnswer>;
    gotoPreviousSlide?: () => void;
}

function SurveyQuestion({
    field,
    // image,
    url,
    answer = null,
    errors = null,
    className,
    onAnswer = null,
    gotoPreviousSlide = null,
}: SurveyQuestionProps) {
    const image = null;
    const { type = null } = field;
    const { value: answerValue = null } = answer || {};
    const [loading, setLoading] = useState(false);
    const [value, setValue] = useState<unknown>(answerValue);
    const {
        url: imageUrl = null,
        width: imageWidth,
        height: imageHeight,
    } = getImageSize(image, 'large') || image || {};
    const formatImageUrl = useFormatImageUrl();

    const postAnswer = useCallback(
        (newAnswer: unknown) => {
            if (onAnswer !== null) {
                setLoading(true);
                onAnswer(
                    newAnswer !== null && (!isString(newAnswer) || newAnswer.length > 0)
                        ? newAnswer
                        : null,
                ).then(() => {
                    setLoading(false);
                });
            }
        },
        [onAnswer],
    );

    const onFieldChange = useCallback(
        (newValue: unknown) => {
            setValue(newValue);
            if (isInstantAnswerField(field)) {
                postAnswer(newValue);
            }
        },
        [setValue, field, postAnswer],
    );

    const onSubmit = useCallback(
        (e: React.FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            postAnswer(value);
        },
        [onAnswer, value],
    );

    useEffect(() => {
        if (answerValue !== null && answerValue !== value) {
            setValue(answerValue);
        }
    }, [answerValue]);

    const hasErrors = errors !== null && errors.length > 0;
    let status = null;
    if (loading) {
        status = 'loading';
    } else if (hasErrors) {
        status = 'error';
    } else if (loading === false && answerValue !== null) {
        status = 'success';
    }

    return (
        <div
            className={classNames([
                styles.container,
                styles[type],
                {
                    [styles.withImage]: image !== null,
                    ['was-validated']: answerValue !== null || hasErrors,
                    [styles.hasErrors]: hasErrors,
                },
                className,
            ])}
        >
            <div className={styles.inner}>
                <form action={url || '#'} className={styles.form} onSubmit={onSubmit}>
                    <FormQuestion
                        field={field}
                        value={value}
                        errors={errors}
                        loading={loading}
                        labelClassName={styles.question}
                        descriptionClassName={styles.description}
                        fieldClassName={styles.field}
                        onChange={onFieldChange}
                    />
                    <div className={styles.buttons}>
                        {gotoPreviousSlide !== null ? (
                            <FormButton
                                type="button"
                                colored
                                transparent
                                className={styles.back}
                                onClick={gotoPreviousSlide}
                            >
                                Retour
                            </FormButton>
                        ) : null}
                        <FormStatus status={status} className={styles.status} />
                        <FormButton type="submit" colored className={styles.submit}>
                            Continuer
                        </FormButton>
                    </div>
                </form>
            </div>
            {imageUrl !== null ? (
                <div className={styles.imageContainer}>
                    <img
                        src={formatImageUrl(imageUrl)}
                        className={styles.image}
                        width={imageWidth}
                        height={imageHeight}
                    />
                </div>
            ) : null}
        </div>
    );
}

export default SurveyQuestion;
